import * as React from 'react'
import styled from 'styled-components'
import {theme} from '../theme'
import {Button} from '../elements/Button'
import {TextArea} from '../elements/TextArea'
import {answerOptions, IAnswer} from '../types/answer'
import {QuestionStore} from '../stores/QuestionStore'
import {AnswerStore} from '../stores/AnswerStore'
import {computed} from 'mobx'
import {observer, inject} from 'mobx-react'
import {RouteComponentProps} from 'react-router'
import {Stores} from '../stores'
import {IQuestion} from '../types/question'
import {NotificationStore} from '../stores/NotificationStore'
import {ConfirmationModal} from '../components/ConfirmationModal'
import {LoaderIcon} from '../elements/LoaderIcon'
import {CandidateStore} from '../stores/CandidateStore'

interface IProps extends RouteComponentProps<{electionId: string; candidateId: string; questionId: string; partyId: string}> {
  questionStore: QuestionStore
  answerStore: AnswerStore
  notifications: NotificationStore
  candidateStore: CandidateStore
}
interface IState {
  answer?: IAnswer
  showConfirmationModal: boolean
  unAnsweredQuestions?: string[]
  okCallback?: () => void
  isSaveAnswerLoading: boolean
}

@inject((stores: Stores) => ({
  answerStore: stores.answerStore,
  questionStore: stores.questionStore,
  notifications: stores.notificationStore,
  candidateStore: stores.candidateStore
}))
@observer
export class QuestionsPage extends React.Component<IProps, IState> {
  public readonly state: IState = {showConfirmationModal: false, isSaveAnswerLoading: false}

  @computed
  get electionId() {
    return Number(this.props.candidateStore.candidate?.election_id) || sessionStorage.getItem('electionId')
  }

  @computed
  get candidateId() {
    return this.props.match.params.candidateId
  }

  @computed
  get questionId() {
    return Number(this.props.match.params.questionId)
  }

  private handleOnChange = (val: any, key: any, answer: Partial<IAnswer>): void => {
    const candidate = this.props.candidateStore.candidate

    if (!candidate?.id || !candidate?.tunnus_id) {
      console.error('Candidate not found') // eslint-disable-line no-console
      return
    }

    this.setState({
      answer: {...answer, candidate_id: candidate.id, question_id: this.questionId, [key]: val, tunnus_id: candidate.tunnus_id}
    })
  }

  private moveIndex = async (newIndex: number) => {
    const newId = this.props.questionStore.questions[newIndex].id
    await this.saveAnswer(`/ehdokas/${this.candidateId}/kysymys/${newId}`)
  }

  private saveAnswer = async (nextUrl?: string, finished = false) => {
    try {
      if (this.state.answer) {
        this.setState({isSaveAnswerLoading: true})
        await this.props.answerStore.upsertAnswer(this.state.answer, Number(this.electionId), this.candidateId)
        this.setState({isSaveAnswerLoading: false})
      }

      const questions = this.props.questionStore.questions
      const answers = this.props.answerStore.answers

      const unAnsweredQuestions = questions.filter(q => {
        const answer = answers.find(a => a.question_id === q.id)
        return !answer || !answer.answer
      })

      if (finished && unAnsweredQuestions.length > 0) {
        this.setState({
          showConfirmationModal: true,
          unAnsweredQuestions: unAnsweredQuestions.map(uq => `Kysymys ${questions.findIndex(q => q.id === uq.id) + 1}`),
          okCallback: this.getOkCallback(nextUrl)
        })
      } else {
        this.setState({answer: undefined})
        nextUrl && this.props.history.push(nextUrl)
      }
    } catch (err) {
      this.setState({isSaveAnswerLoading: false})
      // Store will throw error - don't move forward if answering fails
    }
  }

  private getOkCallback = (nextUrl?: string) => {
    return () => {
      this.setState({showConfirmationModal: false, okCallback: undefined, answer: undefined, unAnsweredQuestions: undefined})
      nextUrl && this.props.history.push(nextUrl)
    }
  }

  private finishAnswers = async () => {
    await this.saveAnswer(`/ehdokas/${this.candidateId}/vastaukset`, true)
  }

  private returnToAnswers = async () => {
    this.props.answerStore.finishEditing()
    await this.saveAnswer(`/ehdokas/${this.candidateId}/vastaukset`)
  }

  private returnToStart = () => {
    this.props.history.push(`/ehdokas/${this.candidateId}`)
  }

  private cancelConfirmation = () => {
    this.setState({showConfirmationModal: false, okCallback: undefined, unAnsweredQuestions: undefined})
  }

  private renderText = (question: IQuestion, index: number) =>
    index % 2 ? (
      <QuestionText aria-label={`Kysymys ${index + 1}`}>
        {index + 1}. {question.text}
      </QuestionText>
    ) : (
      <QuestionTextSafariHack aria-label={`Kysymys ${index + 1}`}>
        {index + 1}. {question.text}
      </QuestionTextSafariHack>
    )

  public render() {
    const {questions} = this.props.questionStore
    const index = questions.findIndex(q => q.id === this.questionId)
    const question = questions[index] || {}
    const answer = this.state.answer || this.props.answerStore.answers.find(a => a.question_id === question.id)
    if (!question) return null

    return (
      <Container>
        <Themes>
          {questions.slice(0, index + 1).map(question => (
            <ThemeColor key={question.id} size={questions.length} />
          ))}
          <QuestionAmount>
            {index + 1}/{questions.length}
          </QuestionAmount>
        </Themes>
        <Content>
          <Theme>{question.category_name}</Theme>
          {this.renderText(question, index)}
          <AnswerOptions>
            <Line />
            {answerOptions.map(option => (
              <OptionContainer key={option.id}>
                <Option
                  onClick={() => this.handleOnChange(option.id, 'answer', answer as IAnswer)}
                  selected={answer && answer.answer === option.id}
                />
                <OptionText id={option.id === 2 || option.id === 4 ? 'middle-option' : 'option'}>{option.text}</OptionText>
              </OptionContainer>
            ))}
          </AnswerOptions>
          <TextArea
            label={'Kirjoita perustelut vastauksellesi'}
            value={answer && answer.explanation}
            rows={window.innerWidth <= theme.breakPoints.mobileMax ? 5 : 8}
            onChange={e => this.handleOnChange(e === '' || e === ' ' ? null : e, 'explanation', answer as IAnswer)}
            charLimit={2500}
          />
          <ActionButtons>
            <Button
              color={'white'}
              onClick={() => (index > 0 ? this.moveIndex(index - 1) : this.returnToStart())}
              height="40px"
              fontSize="18px"
            >
              Edellinen
            </Button>
            {this.props.answerStore.editingAnswer && <Return onClick={this.returnToAnswers}>Tallenna ja palaa yhteenvetoon</Return>}
            {index < questions.length - 1 ? (
              <Button onClick={() => this.moveIndex(index + 1)} height="40px" fontSize="18px">
                {this.state.isSaveAnswerLoading && (
                  <LoaderSpan>
                    <LoaderIcon />
                  </LoaderSpan>
                )}
                <SaveButtonTextSpan isLoading={this.state.isSaveAnswerLoading}>Seuraava</SaveButtonTextSpan>
              </Button>
            ) : (
              <Button onClick={this.finishAnswers} height="40px" fontSize="18px">
                Seuraava
              </Button>
            )}
          </ActionButtons>
        </Content>
        {this.state.showConfirmationModal && (
          <ConfirmationModal
            isOpen={this.state.showConfirmationModal}
            unAnsweredQuestions={this.state.unAnsweredQuestions}
            onOk={() => (this.state.okCallback ? this.state.okCallback() : this.cancelConfirmation())}
          />
        )}
      </Container>
    )
  }
}

const Container = styled.div`
  font-weight: 500;
`

const Return = styled.div`
  color: ${theme.color.red};
  text-decoration: underline;
  cursor: pointer;
  @media screen and (max-width: 600px) {
    order: 3;
    width: 100%;
    text-align: center;
    padding-top: 0.6rem;
  }
`

const QuestionText = styled.div`
  padding-top: 5px;
  min-height: 90px;
  font-size: ${theme.fontSize.large};
  background-color: ${theme.color.contentBackground};
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    min-height: 105px;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    min-height: 105px;
    font-size: 1.7rem;
  }
`

const QuestionTextSafariHack = styled.span`
  display: block;
  padding-top: 5px;
  min-height: 90px;
  font-size: ${theme.fontSize.large};
  background-color: ${theme.color.contentBackground};
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    min-height: 105px;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    min-height: 105px;
    font-size: 1.7rem;
  }
`

const QuestionAmount = styled.div`
  margin: auto 0 auto auto;
  width: 50px;
  text-align: center;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    width: 35px;
  }
`

const Themes = styled.div`
  display: flex;
  justify-content: flex-start;
  background-color: #ddd;
  height: 31px;
  width: 100%;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    height: 22px;
    font-size: 1.5rem;
    font-weight: 600;
  }
`

const ThemeColor = styled.div<{size: number}>`
  background-color: ${theme.color.red};
  margin: 0;
  width: 0%;
  animation: categorySlide 0.5s forwards;
  animation-delay: 0s;
  @keyframes categorySlide {
    to {
      width: calc((100% - 50px) / ${props => props.size});
    }
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    @keyframes categorySlide {
      to {
        width: calc((100% - 35px) / ${props => props.size});
      }
    }
  }
`

const Line = styled.div`
  width: 81%;
  height: 1px;
  background-color: #707070;
  position: absolute;
  left: 0;
  right: 0;
  top: 16px;
  margin: auto;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    top: 14px;
  }
`

const AnswerOptions = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
  padding-bottom: 5.2rem;
  width: calc(100% + 80px);
  margin-left: -40px;
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    width: calc(100% + 40px);
    margin-left: -20px;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    padding-bottom: 2rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobilePortrait}px) {
    width: calc(100% + 20px);
    margin-left: -10px;
  }
`

const OptionContainer = styled.div`
  width: calc(100% / 5);
  text-align: center;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    font-size: ${theme.fontSize.small};
    #middle-option {
      opacity: 0;
    }
  }
`

const OptionText = styled.div`
  font-size: 1.9rem;
  padding-top: 0.9rem;
  margin: auto;
  max-width: 62px;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    font-size: 1.5rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    max-width: 50px;
  }
`

const Option = styled.div<{selected?: boolean}>`
  background-color: ${props => (props.selected ? theme.color.red : theme.color.contentBackground)};
  border: ${props => (props.selected ? `1px solid ${theme.color.red}` : '1px solid #222')};
  height: 33px;
  width: 33px;
  border-radius: 50%;
  position: relative;
  z-index: 99;
  cursor: pointer;
  margin: 0 auto;
`

const Theme = styled.p`
  display: inline-block;
  line-height: 1.3;
  border-bottom: 2px solid #222;
  margin: 0;
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    display: none;
  }
`

const Content = styled.div`
  padding: 2rem 12.8rem 5.5rem 13.2rem;
  ${TextArea} {
    margin-bottom: 3.8rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    padding: 2rem 4rem 5.5rem 4rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    padding: 0.5rem 2rem 2rem 2rem;
    ${TextArea} {
      margin-bottom: 2rem;
    }
  }
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`

const LoaderSpan = styled.span`
  position: absolute;
  right: 0;
  left: 0;
`

const SaveButtonTextSpan = styled.span<{isLoading: boolean}>`
  ${({isLoading}) => isLoading && 'visibility:hidden;'}
`
